import React, {useContext, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Button,
    Check,
    Grid,
    GridColumn,
    Modal,
    ModalCloser,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Spinner,
    TextField,
} from '../../../patterns';
import {StoreContext} from '../base/root-store';
import {
    IntegrationConfig,
    IntegrationConfigMasked,
} from './company-page-store';

interface IntegrationConfigModalProps {
    initialSettings?: IntegrationConfigMasked;
    titleLabel: string;
    apiKeyLabel: string;
    authKeyId: string;
    disconnectLabel: string;
    disconnectConfirmLabel: string;
    saveConfig: (config: IntegrationConfig) => void;
    revokeConfig: () => Promise<void>;
    logoSrc?: string;
    logoAlt?: string;
}

export const IntegrationConfigModal = observer((props: IntegrationConfigModalProps): JSX.Element => {
    const store = useContext(StoreContext).companyStore;
    const [authKey, setAuthKey] = useState<string>(
        props.initialSettings?.maskedAuthKey ? props.initialSettings.maskedAuthKey : '',
    );
    const [isAuthKeyModified, setAuthKeyModified] = useState(false);
    const [syncAbsences, setSyncAbsences] = useState<boolean>(
        props.initialSettings ? props.initialSettings.syncAbsences : true,
    );
    const [syncEmployeeData, setSyncEmployeeData] = useState<boolean>(
        props.initialSettings ? props.initialSettings.syncEmployeeData : false,
    );
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const hasSyncOption = syncAbsences || syncEmployeeData;

    return (
        <Modal size="small">
            <ModalHeader>
                <h4>{props.titleLabel}</h4>
            </ModalHeader>
            <ModalContent>
                <div className="text">
                    {props.logoSrc && (
                        <div style={{display: 'flex', justifyContent: 'center', marginBottom: '1rem'}}>
                            <img
                                src={props.logoSrc}
                                alt={props.logoAlt}
                                style={{width: '4rem', height: '4rem', borderRadius: '8px'}}
                            />
                        </div>
                    )}
                    <div className="input-wrapper">
                        <TextField
                            label={props.apiKeyLabel}
                            value={authKey}
                            onChange={(value) => {
                                setAuthKey(value);
                                setAuthKeyModified(true);
                            }}
                            id={props.authKeyId}
                            name={props.authKeyId}
                        />
                    </div>
                    <div style={{marginTop: '1rem'}}>
                        <h5>{store.label('company.integration-config.sync-options.title')}</h5>
                        <Check
                            label={store.label('company.integration-config.sync-absences')}
                            checked={syncAbsences}
                            onChange={(checked): void => setSyncAbsences(checked)}
                            id={`${props.authKeyId}-sync-absences`}
                            name={`${props.authKeyId}-sync-absences`}
                            value="sync-absences"
                        />
                        <Check
                            label={store.label('company.integration-config.sync-employee-data')}
                            checked={syncEmployeeData}
                            onChange={(checked): void => setSyncEmployeeData(checked)}
                            id={`${props.authKeyId}-sync-employee-data`}
                            name={`${props.authKeyId}-sync-employee-data`}
                            value="sync-employee-data"
                        />
                        {!hasSyncOption && (
                            <p className="validation__message">
                                {store.label('company.integration-config.sync-options.validation')}
                            </p>
                        )}
                    </div>
                </div>
            </ModalContent>
            <ModalFooter>
                <Grid wrap={['xs']} justify={['between-xs']}>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <ModalCloser>
                            <Button
                                block={true}
                                color="white"
                                text={store.label('company.modal.cancel')}
                            />
                        </ModalCloser>
                    </GridColumn>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        {props.initialSettings && (
                            <div className="button-with-spinner" style={{marginBottom: '0.75rem'}}>
                                <Button
                                    block={true}
                                    color="red"
                                    disabled={isDisconnecting}
                                    text={props.disconnectLabel}
                                    onClick={async () => {
                                        if (window.confirm(props.disconnectConfirmLabel)) {
                                            setIsDisconnecting(true);
                                            try {
                                                await props.revokeConfig();
                                            } finally {
                                                setIsDisconnecting(false);
                                            }
                                        }
                                    }}
                                />
                                {isDisconnecting && <Spinner className="button-with-spinner__spinner" />}
                            </div>
                        )}
                        <Button
                            block={true}
                            color="green"
                            text={store.label('company.modal.save')}
                            disabled={!authKey.trim() || !hasSyncOption}
                            onClick={() => {
                                props.saveConfig({
                                    companyId: store.payload?.companySettingsPayload.companyId,
                                    authKey: isAuthKeyModified ? authKey.trim() : undefined,
                                    syncAbsences,
                                    syncEmployeeData,
                                });
                            }}
                        />
                    </GridColumn>
                </Grid>
            </ModalFooter>
        </Modal>
    );
});
